import { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Alert, Button, CircularProgress, Typography } from '@mui/material'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getAllProducts,
  getMetaUserRolesByProductId,
} from 'src/supportPortal/services/docSideService'
import ContentManagement from './components/ContentManagement'
import { DocsideCmsContext } from 'src/supportPortal/context/DocsideCmsContextProvider'

export default function DocSideContent() {
  const { productPackageId, roleId, contextId } = useParams()
  const navigate = useNavigate()
  const { products, contexts, loadingContexts, errorContexts, loadContexts } =
    useContext(DocsideCmsContext)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [role, setRole] = useState(null)

  const selectedContext = contexts?.find((c) => c.id === contextId)
  const selectedProduct = products?.find((p) => p.id === productPackageId)

  // Load product and role on component mount
  useEffect(() => {
    loadProductAndRole()
  }, [contextId, roleId])

  const loadProductAndRole = async () => {
    setLoading(true)
    setError(null)
    try {
      // Load products to find the selected one
      const rolesResponse = await getMetaUserRolesByProductId(contextId)
      if (rolesResponse?.status === 200) {
        const roles = rolesResponse.data || []
        const selectedRole = roles.find((r) => r.id === roleId)

        if (!selectedRole) {
          setError(`Role "${roleId}" not found for this product`)
          return
        }

        setRole(selectedRole)
      } else {
        setError('Failed to load roles')
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    navigate(`/support-home/doc-side/${productPackageId}/contexts/${contextId}/roles`)
  }

  // Handle invalid product or role
  if (!loading && !loadingContexts && !selectedContext && !role && !error) {
    return (
      <div className="d-flex flex-column h-100">
        <SupportHeader
          heading="DocSide - Invalid Selection"
          actionButton={
            <Button startIcon={<ArrowBack />} onClick={handleBack} variant="outlined" size="small">
              Back to Roles
            </Button>
          }
        />
        <Box sx={{ flex: 1, p: 3, maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Invalid Selection
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {!selectedContext && `The context "${contextId}" was not found.`}
              {selectedContext && !role && `The role "${roleId}" was not found for this product.`}
            </Typography>
            <Button variant="contained" onClick={handleBack}>
              Go to Roles
            </Button>
          </Box>
        </Box>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column h-100">
      <SupportHeader
        heading={role?.title || 'Role'}
        subHeading="Content Management"
        showBackButton
      />

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: '1400px',
          width: '100%',
          margin: '0 auto',
          background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        {/* Global Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setError(null)
                  loadProductAndRole()
                }}
                sx={{ textTransform: 'none' }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {(loading || loadingContexts) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && selectedContext && role && (
          <ContentManagement context={selectedContext} product={selectedProduct} role={role} />
        )}
      </Box>
    </div>
  )
}
